import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { searchTickers } from "@/lib/api";
import { Search, Menu, X } from "lucide-react";
import ThresholdsDialog from "./ThresholdsDialog";
import ThemeToggle from "./ThemeToggle";
import AuthButton from "./AuthButton";
import LanguageToggle from "./LanguageToggle";
import DataSourceToggle from "./DataSourceToggle";
import HelpChat from "./HelpChat";
import BackButton from "./BackButton";
import ScrollRestoration from "./ScrollRestoration";
import LoginNudge from "./LoginNudge";
import PageExportButton from "./PageExportButton";
import FeedbackControls from "./feedback/FeedbackControls";
import { useI18n } from "@/lib/i18n";

export default function Layout({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useI18n();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const timer = useRef(null);
    const boxRef = useRef(null);

    useEffect(() => { setMenuOpen(false); }, [location.pathname]);

    // Mobile rotation zoom bug workaround: after opening/closing a full-screen `fixed`
    // overlay (e.g. an enlarged chart) in landscape, some Android/Chromium browsers keep
    // the old (wide) layout-viewport width when rotating back to portrait, so the whole
    // page renders zoomed-out in the left half. On orientationchange we briefly append
    // `maximum-scale=1` to the viewport meta to force the browser to recompute the layout
    // width/scale, then restore it so pinch-zoom stays available.
    useEffect(() => {
        const vp = document.querySelector('meta[name="viewport"]');
        if (!vp) return;
        const base = vp.getAttribute("content");
        const onOrient = () => {
            vp.setAttribute("content", base + ", maximum-scale=1");
            setTimeout(() => vp.setAttribute("content", base), 350);
        };
        window.addEventListener("orientationchange", onOrient);
        return () => window.removeEventListener("orientationchange", onOrient);
    }, []);

    // Collapse the header into the single-row hamburger layout when width is small
    // OR the device is in landscape with little height (phones/foldables held sideways),
    // so rows 2 & 3 don't eat the scarce vertical space in landscape.
    const [compact, setCompact] = useState(typeof window !== "undefined" && window.innerWidth < 768);
    useEffect(() => {
        if (typeof window === "undefined") return;
        const check = () => {
            const w = window.innerWidth, h = window.innerHeight;
            setCompact(w < 768 || (w > h && h <= 600));
        };
        check();
        window.addEventListener("resize", check);
        window.addEventListener("orientationchange", check);
        return () => { window.removeEventListener("resize", check); window.removeEventListener("orientationchange", check); };
    }, []);

    useEffect(() => {
        if (!query.trim()) { setResults([]); return; }
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            try {
                const r = await searchTickers(query);
                setResults(r.results || []);
                setShowResults(true);
            } catch { setResults([]); }
        }, 250);
    }, [query]);

    useEffect(() => {
        const handler = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setShowResults(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const go = (ticker) => {
        setQuery("");
        setResults([]);
        setShowResults(false);
        navigate(`/company/${ticker}`);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (query.trim()) go(query.trim().toUpperCase());
    };

    const navClass = (path) => `text-xs uppercase tracking-[0.15em] font-semibold px-3 py-1 ${
        location.pathname === path || location.pathname.startsWith(path + "/")
            ? "bg-black text-[#FDF1E6]"
            : "text-black hover:bg-black hover:text-[#FDF1E6]"
    } transition-colors`;

    const navClassMobile = (path) => `block text-sm uppercase tracking-[0.12em] font-semibold px-3 py-2.5 ${
        location.pathname === path || location.pathname.startsWith(path + "/")
            ? "bg-black text-[#FDF1E6]"
            : "text-black hover:bg-black/5"
    } transition-colors`;

    const links = [
        { to: "/", label: t("nav.home"), testid: "home" },
        { to: "/portfolio", label: t("nav.portfolio"), testid: "portfolio" },
        { to: "/watchlist", label: t("nav.watchlist"), testid: "watchlist" },
        { to: "/compare", label: t("nav.compare"), testid: "compare" },
        { to: "/thesis", label: t("nav.thesis"), testid: "thesis" },
        { to: "/visual", label: "Visual", testid: "visual" },
        { to: "/kpis", label: "KPIs", testid: "kpis" },
        { to: "/macro", label: "Macro", testid: "macro" },
    ];

    return (
        <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-base)" }}>
            <ScrollRestoration />
            <header className="border-b border-black sticky top-0 z-40" style={{ background: "var(--bg-base)" }}>
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                    {/* Row 1: Logo · Search · Auth (wraps on mobile) */}
                    <div className="py-3 flex items-center gap-3 sm:gap-6 border-b border-black/10 flex-wrap">
                        <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0 order-1" data-testid="nav-home">
                            <div className="w-8 h-8 border border-black flex items-center justify-center bg-white shrink-0">
                                <div className="w-3 h-3 bg-[#052049]" />
                            </div>
                            <div className="leading-none">
                                <div className="font-serif text-xl sm:text-2xl font-medium">VegaValor</div>
                                <div className="overline text-[#4A4A4A] hidden sm:block">Equity Research</div>
                            </div>
                        </Link>

                        {compact && (
                        <div className="order-2 ml-auto flex items-center gap-2">
                            <FeedbackControls mobile />
                            <AuthButton />
                            <button
                                className="border border-black bg-white p-2"
                                onClick={() => setMenuOpen((o) => !o)}
                                aria-label="Menú"
                                data-testid="nav-mobile-toggle"
                            >
                                {menuOpen ? <X size={18} /> : <Menu size={18} />}
                            </button>
                        </div>
                        )}

                        <div className="order-3 sm:order-2 w-full sm:w-auto sm:flex-1 flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 min-w-0">
                        <div ref={boxRef} className="flex-1 min-w-[180px] sm:min-w-0 relative">
                            <form onSubmit={handleSubmit} className="flex items-center border border-black bg-white" data-testid="search-form">
                                <Search size={16} className="ml-3 text-[#4A4A4A] shrink-0" />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("nav.search_placeholder")}
                                    className="flex-1 min-w-0 px-3 py-2 outline-none font-mono text-sm bg-transparent"
                                    data-testid="search-input"
                                    onFocus={() => results.length && setShowResults(true)}
                                />
                                <button type="submit" className="btn-primary !py-2 !px-3 shrink-0" data-testid="search-submit">{t("nav.search_button")}</button>
                            </form>
                            {showResults && results.length > 0 && (
                                <div className="absolute left-0 right-0 top-full bg-white border border-black border-t-0 max-h-80 overflow-auto z-50" data-testid="search-results">
                                    {results.map((r, i) => (
                                        <button
                                            key={i}
                                            onClick={() => go(r.symbol)}
                                            className="w-full text-left px-3 py-2 hover:bg-[#F5E4D4] border-b border-black/10 flex justify-between items-center text-sm"
                                            data-testid={`search-result-${r.symbol}`}
                                        >
                                            <div>
                                                <span className="font-mono font-semibold">{r.symbol}</span>
                                                <span className="ml-2 text-[#4A4A4A]">{r.name}</span>
                                            </div>
                                            <span className="overline text-[#4A4A4A]">{r.exchange}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <DataSourceToggle />
                        </div>

                        {!compact && (
                        <div className="shrink-0 order-4">
                            <AuthButton />
                        </div>
                        )}
                    </div>

                    {/* Row 2 (desktop): Nav links · Utility toggles */}
                    {!compact && (
                    <div className="py-2 flex items-center justify-between gap-4 flex-wrap">
                        <nav className="flex items-center gap-1 flex-wrap">
                            {links.map((l) => (
                                <Link key={l.to} to={l.to} className={navClass(l.to)} data-testid={`nav-link-${l.testid}`}>{l.label}</Link>
                            ))}
                        </nav>
                        <div className="flex items-center gap-1 flex-wrap">
                            <FeedbackControls />
                            <HelpChat />
                            <ThresholdsDialog />
                            <PageExportButton />
                            <LanguageToggle />
                            <ThemeToggle />
                        </div>
                    </div>
                    )}

                    {/* Mobile menu (drawer) */}
                    {compact && menuOpen && (
                        <div className="py-2 border-t border-black/10" data-testid="nav-mobile-menu">
                            <nav className="flex flex-col">
                                {links.map((l) => (
                                    <Link key={l.to} to={l.to} className={navClassMobile(l.to)} data-testid={`nav-mlink-${l.testid}`}>{l.label}</Link>
                                ))}
                            </nav>
                            <div className="flex items-center gap-1 flex-wrap mt-2 pt-2 border-t border-black/10">
                                <HelpChat />
                                <ThresholdsDialog />
                                <PageExportButton />
                                <LanguageToggle />
                                <ThemeToggle />
                            </div>
                        </div>
                    )}
                </div>
            </header>

            <main className="flex-1 max-w-[1400px] w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <LoginNudge />
                <BackButton />
                {children}
            </main>

            <footer className="border-t border-black/30 py-4 px-4 sm:px-6 text-xs text-[#4A4A4A]" data-testid="footer">
                <div className="max-w-[1400px] mx-auto space-y-2">
                    <div className="border border-[#B32A22]/40 bg-white/60 px-3 py-2 text-[11px] leading-relaxed" data-testid="legal-disclaimer">
                        <span className="font-mono font-semibold text-[#B32A22] mr-1">{t("footer.legal_tag")}</span>
                        {t("footer.legal")}
                    </div>
                    <div className="flex justify-between gap-2">
                        <span>VegaValor · Precios: Marketstack · Fundamentales: Yahoo Finance</span>
                        <span className="font-mono">v1.0</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
